import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import EmployeeDashboard from './pages/EmployeeDashboard';
import AdminDashboard from './pages/AdminDashboard';
import MechanicDashboard from './pages/MechanicDashboard';
import { CircleNotch } from '@phosphor-icons/react';
import PresenceLayer from './components/PresenceLayer';

/** Redirect authenticated users to their role dashboard while preserving
 *  the current URL search string (e.g. ?tab=przypomnienia&snooze=<id>).
 *  Used by "/" and the catch-all "*" routes so push-notification deep-links work.
 */
function AuthedRedirect({ fallback = '/login' }) {
    const { user } = useAuth();
    const location = useLocation();
    if (!user) return <Navigate to={fallback} replace />;
    return <Navigate to={{ pathname: loginRedirectFor(user.role), search: location.search }} replace />;
}

function ProtectedRoute({ children, allowedRoles = null }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
        const fallback =
            user.role === 'admin' ? '/admin'
            : user.role === 'mechanic' ? '/mechanic'
            : '/dashboard';
        return <Navigate to={fallback} replace />;
    }

    return children;
}

function loginRedirectFor(role) {
    if (role === 'admin') return '/admin';
    if (role === 'mechanic') return '/mechanic';
    return '/dashboard';
}

function AppRoutes() {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <PresenceLayer user={user}>
        <Routes>
            <Route path="/login" element={user ? <AuthedRedirect /> : <Login />} />
            <Route path="/register" element={user ? <AuthedRedirect /> : <Register />} />
            <Route path="/" element={<AuthedRedirect />} />
            <Route path="/dashboard" element={
                <ProtectedRoute allowedRoles={['employee']}>
                    <EmployeeDashboard />
                </ProtectedRoute>
            } />
            <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                </ProtectedRoute>
            } />
            <Route path="/mechanic" element={
                <ProtectedRoute allowedRoles={['mechanic']}>
                    <MechanicDashboard />
                </ProtectedRoute>
            } />
            <Route path="*" element={<AuthedRedirect />} />
        </Routes>
        </PresenceLayer>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
